// validation/dependencyUtils.ts

import { SurveyModel, Question } from "survey-core";

/* =====================================================
   1️⃣ MATCH ENGINE (already exists, extended slightly)
===================================================== */

export function dependencyMatches(parentVal: any, dependsValue: any): boolean {
  if (dependsValue === undefined || dependsValue === null) return false;
  if (parentVal === undefined || parentVal === null) return false;

  // Array (checkbox, tagbox)
  if (Array.isArray(parentVal)) {
    return parentVal.some((item) => {
      if (item && typeof item === "object") {
        return Object.values(item).some(v => String(v) === String(dependsValue));
      }
      return String(item) === String(dependsValue);
    });
  }

  // Boolean
  if (typeof parentVal === "boolean") {
    return String(parentVal) === String(dependsValue);
  }

  // Number
  if (typeof parentVal === "number") {
    const num = Number(dependsValue);
    return !Number.isNaN(num) && parentVal === num;
  }

  // Object (matrix row / multipletext)
  if (typeof parentVal === "object") {
    return Object.values(parentVal).some(v => String(v) === String(dependsValue));
  }

  // Default string
  return String(parentVal) === String(dependsValue);
}

/* =====================================================
   2️⃣ APPLY SHOW / ENABLE EXPRESSIONS (ON LOAD)
===================================================== */

export function applyDependencyVisibility(survey: SurveyModel) {
  survey.getAllQuestions().forEach((q: any) => {
    if (!q.dependsOn || q.dependsValue === undefined) return;

    const expr = `{${q.dependsOn}} = '${String(q.dependsValue).replace(/'/g, "\\'")}'`;

    if (q.dependencyAction === "show") {
      q.visibleIf = expr;
    }

    if (q.dependencyAction === "enable") {
      q.enableIf = expr;
    }
  });
}

/* =====================================================
   3️⃣ REQUIRE VALIDATION
===================================================== */

export function runDependencyRequireValidation(
  survey: SurveyModel,
  question: any,
  value: any
): string | null {

  if (question.dependencyAction !== "require") return null;
  if (!question.dependsOn) return null;

  const parentVal = survey.getValue(question.dependsOn);
  const matches = dependencyMatches(parentVal, question.dependsValue);

  if (!matches) return null;

  const empty =
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);

  if (empty) {
    return (
      question.dependsError ||
      `This field is required when "${question.dependsOn}" = "${question.dependsValue}".`
    );
  }

  return null;
}

/* =====================================================
   4️⃣ SET VALUE + CLEAR WHEN HIDDEN
===================================================== */

export function handleDependencyValueChange(
  survey: SurveyModel,
  changedName: string
) {
  survey.getAllQuestions().forEach((q: any) => {
    if (!q.dependsOn || q.dependsOn !== changedName) return;

    const parentVal = survey.getValue(q.dependsOn);
    const matches = dependencyMatches(parentVal, q.dependsValue);

    // SET VALUE
    if (q.dependencyAction === "setValue") {
      if (matches) {
        const cur = survey.getValue(q.name);
        const empty =
          cur === undefined ||
          cur === null ||
          cur === "" ||
          (Array.isArray(cur) && cur.length === 0);

        if (empty && q.dependsSetValue !== undefined && q.dependsSetValue !== "") {
          survey.setValue(q.name, q.dependsSetValue);
        }
      }
    }

    // CLEAR WHEN HIDDEN (optional behavior)
    if (!matches && q.clearWhenHidden) {
      survey.setValue(q.name, undefined);
    }
  });
}
